import React, { useState, useMemo, useEffect } from "react";
import {
  FaArrowLeft,
  FaCogs,
  FaSlidersH,
  FaUndo,
  FaCalculator,
} from "react-icons/fa";
import {
  productNames,
  defaultMachinePrices,
  okkaliUnitPrices,
  machineModelOptions,
} from "./constants";
import {
  cleanCurrency,
  getDefaultMachineInputs,
  getDefaultGeneralValues,
} from "./calculationUtils";
import { TextInput, CustomDropdown } from "../atoms";
import { Drawer } from "../shared";

const formatParamLabel = (key) => {
  // Türkçe karakter düzeltmeleri için mapping
  const labelMap = {
    sekerBirimFiyat: "Şeker Birim Fiyat",
    sekerGr: "Şeker (gr)",
    cayKgFiyat: "Çay Kg Fiyat",
    turkKahveKgFiyat: "Türk Kahvesi Kg Fiyat",
    filtreKahveKgFiyat: "Filtre Kahve Kg Fiyat",
    espressoKgFiyat: "Espresso Kg Fiyat",
    turkKahvesiSatisFiyat: "Türk Kahvesi Satış Fiyat",
    filtreKahveSatisFiyat: "Filtre Kahve Satış Fiyat",
    espressoSatisFiyat: "Espresso Satış Fiyat",
    cayKahveGr: "Çay/Kahve (gr)",
    kagitBardak: "Kağıt Bardak",
    kagitBardakKatsayi: "Kağıt Bardak Katsayı",
    elemanMaliyet: "Eleman Maliyet",
    elemanSayisi: "Eleman Sayısı",
    elemanIhtiyaci: "Eleman İhtiyacı",
    elemanVerimliligi: "Eleman Verimliliği",
    kullaniciPersonelVerimliligi: "Kullanıcı Personel Verimliliği",
    kayipKacakOrani: "Kayıp/Kaçak Oranı",
    servisBakimMaliyeti: "Servis Bakım Maliyeti",
    makineOmru: "Makine Ömrü",
  };

  // Eğer mapping'de varsa direkt kullan
  if (labelMap[key]) {
    return labelMap[key];
  }

  // Yoksa otomatik formatla
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
};

const formatPriceWithTwoDecimals = (value) => {
  if (value === null || value === undefined) return "";
  const stringValue = value.toString().replace(",", ".").trim();
  if (stringValue === "") {
    return "";
  }
  const numericValue = parseFloat(stringValue);
  if (!Number.isFinite(numericValue)) {
    return "";
  }
  // Eğer tam sayıysa ondalık kısmı gösterme
  if (numericValue % 1 === 0) {
    return numericValue.toFixed(0);
  }
  return numericValue.toFixed(2);
};

const paramButtonClass =
  "flex-1 flex items-center justify-center gap-2 px-3 py-2.5 text-xs sm:text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl shadow-sm hover:bg-gray-50 hover:shadow-md transition-all duration-200";

export default function CalculationStep({
  selectedProducts,
  productStates,
  agreementData,
  machineInputParams,
  generalParams,
  onUpdateProductInput,
  onUpdateAgreementData,
  onUpdateMachineInputParams,
  onUpdateGeneralParams,
  onCalculate,
  onReset,
  onBack,
}) {
  const [drawerState, setDrawerState] = useState({
    open: false,
    type: null,
    product: null,
  });
  const [drawerForm, setDrawerForm] = useState({});
  const [unitPriceInputs, setUnitPriceInputs] = useState({});

  useEffect(() => {
    setUnitPriceInputs({});
  }, [selectedProducts]);

  const openDrawer = (type, product) => {
    const source =
      type === "machine"
        ? machineInputParams[product] || getDefaultMachineInputs(product)
        : generalParams[product] || getDefaultGeneralValues(product);

    setDrawerForm({ ...source });
    setDrawerState({ open: true, type, product });
  };

  const closeDrawer = () => {
    setDrawerState({ open: false, type: null, product: null });
    setDrawerForm({});
  };

  const handleDrawerChange = (key, value) => {
    setDrawerForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleDrawerSave = () => {
    const { product, type } = drawerState;
    if (!product || !type) return;

    // Tüm alanlar tek çağrıda gönderilir; alan alan gönderilirse aynı batch
    // içinde eski parametre objesi okunur ve sadece son alan kaydedilirdi.
    if (type === "machine") {
      onUpdateMachineInputParams(product, { ...drawerForm });
    } else {
      onUpdateGeneralParams(product, { ...drawerForm });
    }

    closeDrawer();
  };

  const drawerFields = useMemo(
    () =>
      Object.entries(drawerForm).map(([key, value]) => ({
        key,
        label: formatParamLabel(key),
        value,
      })),
    [drawerForm]
  );

  const drawerTitle =
    drawerState.type === "machine"
      ? "Makine Parametreleri"
      : drawerState.type === "general"
      ? "Genel Parametreler"
      : "";

  const renderProductCard = (product) => {
    const state = productStates[product] || {};
    const productName = productNames[product];
    const agreementInfo = agreementData?.[product] || {};
    const modelOptions = machineModelOptions[product] || [];
    const handleInputChange = (key, value) =>
      onUpdateProductInput(product, key, value);
    const resolvedUnitPrice =
      agreementInfo.birimAdetFiyat ??
      state.okkaliBirimFiyat ??
      okkaliUnitPrices[product] ??
      "";
    const defaultUnitPriceValue =
      resolvedUnitPrice === ""
        ? ""
        : formatPriceWithTwoDecimals(resolvedUnitPrice);
    const hasCustomInput = Object.prototype.hasOwnProperty.call(
      unitPriceInputs,
      product
    );
    const unitPriceValue = hasCustomInput
      ? unitPriceInputs[product]
      : defaultUnitPriceValue;
    const selectedModel = agreementInfo.makineModeli || modelOptions[0] || "";

    const handleUnitPriceChange = (nextValue) => {
      setUnitPriceInputs((prev) => ({
        ...prev,
        [product]: nextValue,
      }));
    };

    const handleUnitPriceBlur = () => {
      const rawValue = hasCustomInput
        ? unitPriceInputs[product]
        : defaultUnitPriceValue;
      const cleanedValue = cleanCurrency(rawValue || "").replace(
        /[^\d.,-]/g,
        ""
      );

      if (cleanedValue === "") {
        setUnitPriceInputs((prev) => ({
          ...prev,
          [product]: "",
        }));
        if (onUpdateAgreementData) {
          onUpdateAgreementData(product, "birimAdetFiyat", "");
        }
        handleInputChange("okkaliBirimFiyat", "");
        return;
      }

      const normalizedValue = formatPriceWithTwoDecimals(
        cleanedValue.replace(",", ".")
      );

      setUnitPriceInputs((prev) => ({
        ...prev,
        [product]: normalizedValue,
      }));

      if (onUpdateAgreementData) {
        onUpdateAgreementData(product, "birimAdetFiyat", normalizedValue);
      }
      handleInputChange("okkaliBirimFiyat", normalizedValue);
    };

    const handleModelChange = (option) => {
      const nextModel = option?.label || "";
      if (onUpdateAgreementData) {
        onUpdateAgreementData(product, "makineModeli", nextModel);
      }
    };

    return (
      <div
        key={product}
        className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 sm:p-5 flex flex-col gap-4"
      >
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-base font-bold text-gray-900">{productName}</h3>
          <span className="text-[11px] font-semibold text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-full px-2.5 py-1">
            {selectedModel || "Model seçilmedi"}
          </span>
        </div>

        <TextInput
          label={`${productName} - Aylık Tüketim Adeti`}
          type="number"
          value={state.tuketim || ""}
          onChange={(e) => handleInputChange("tuketim", e.target.value)}
          placeholder="Aylık tüketim adedi"
          wrapperClassName="!space-y-2"
        />

        <TextInput
          label="Birim Fiyatı (₺)"
          value={unitPriceValue}
          onChange={(e) => handleUnitPriceChange(e.target.value)}
          onBlur={handleUnitPriceBlur}
          placeholder="Örn. 4,50"
          wrapperClassName="!space-y-2"
        />

        <CustomDropdown
          label="Makine Modeli"
          options={modelOptions.map((option) => ({
            id: option,
            label: option,
          }))}
          value={
            selectedModel ? { id: selectedModel, label: selectedModel } : null
          }
          placeholder="Makine modeli seçin"
          showSearch={false}
          onChange={handleModelChange}
        />

        <div className="flex gap-3">
          <div className="flex-1">
            <TextInput
              label="Okkalı Makine Birim Fiyatı (₺)"
              value="Bedelsiz"
              disabled
              className="!bg-gray-100 !cursor-not-allowed"
              wrapperClassName="!space-y-2"
            />
          </div>
          <div className="flex-1">
            <TextInput
              label="Diğer Makine Birim Fiyatı (₺)"
              value={
                state.digerFiyat === ""
                  ? ""
                  : formatPriceWithTwoDecimals(
                      state.digerFiyat !== undefined && state.digerFiyat !== null
                        ? state.digerFiyat
                        : defaultMachinePrices[product]
                    )
              }
              onChange={(e) =>
                handleInputChange("digerFiyat", cleanCurrency(e.target.value))
              }
              placeholder="0"
              wrapperClassName="!space-y-2"
            />
          </div>
        </div>

        <div className="flex gap-3">
          <TextInput
            label="Okkalı Makine Adedi"
            type="number"
            value={state.makineAdetOkkali || ""}
            onChange={(e) =>
              handleInputChange("makineAdetOkkali", e.target.value)
            }
            placeholder="0"
            wrapperClassName="!space-y-2 flex-1"
          />
          <TextInput
            label="Diğer Makine Adedi"
            type="number"
            value={state.makineAdetDiger || ""}
            onChange={(e) => handleInputChange("makineAdetDiger", e.target.value)}
            placeholder="0"
            wrapperClassName="!space-y-2 flex-1"
          />
        </div>

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={() => openDrawer("machine", product)}
            className={paramButtonClass}
          >
            <FaCogs className="w-3.5 h-3.5 text-indigo-500" />
            <span>Makine Parametreleri</span>
          </button>
          <button
            type="button"
            onClick={() => openDrawer("general", product)}
            className={paramButtonClass}
          >
            <FaSlidersH className="w-3.5 h-3.5 text-indigo-500" />
            <span>Genel Parametreler</span>
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="space-y-5">
        {selectedProducts.length === 0 && (
          <div className="p-8 rounded-2xl border border-dashed border-gray-300 bg-gray-50 text-center text-sm text-gray-500">
            Ürün seçimi tamamlanmadı. Öncelikle teklif oluşturmak istediğiniz
            ürünleri seçin.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {selectedProducts.map(renderProductCard)}
        </div>

        {/* Adım aksiyonları */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl shadow-sm hover:bg-gray-50 transition-colors"
            >
              <FaArrowLeft className="w-3.5 h-3.5" />
              <span>Geri</span>
            </button>
          ) : (
            <span />
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => {
                setUnitPriceInputs({});
                onReset();
              }}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl shadow-sm hover:bg-amber-100 transition-colors"
            >
              <FaUndo className="w-3.5 h-3.5" />
              <span>Sıfırla</span>
            </button>
            <button
              type="button"
              onClick={onCalculate}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-sm hover:shadow-md hover:from-indigo-700 hover:to-purple-700 transition-all duration-200"
            >
              <FaCalculator className="w-3.5 h-3.5" />
              <span>Hesapla</span>
            </button>
          </div>
        </div>
      </div>

      <Drawer
        open={drawerState.open}
        onClose={closeDrawer}
        title={drawerTitle}
        subtitle={
          drawerState.product ? productNames[drawerState.product] : undefined
        }
        titleIcon={
          drawerState.type === "machine" ? (
            <FaCogs className="w-4 h-4 text-indigo-600" />
          ) : (
            <FaSlidersH className="w-4 h-4 text-indigo-600" />
          )
        }
        width="sm"
      >
        <Drawer.Body className="px-5 py-4 space-y-4">
          {drawerFields.length === 0 && (
            <p className="text-sm text-gray-400">Parametre bulunamadı.</p>
          )}

          {drawerFields.map((field) => (
            <TextInput
              key={field.key}
              label={field.label}
              value={field.value || ""}
              onChange={(e) => handleDrawerChange(field.key, e.target.value)}
              placeholder={
                field.key === "elemanSayisi"
                  ? "Eleman Sayısını Girin"
                  : "Değer girin"
              }
              wrapperClassName="!space-y-2"
            />
          ))}
        </Drawer.Body>

        <Drawer.Footer className="px-5 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={closeDrawer}
            className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
          >
            Vazgeç
          </button>
          <button
            type="button"
            onClick={handleDrawerSave}
            className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-sm hover:shadow-md hover:from-indigo-700 hover:to-purple-700 transition-all duration-200"
          >
            Kaydet
          </button>
        </Drawer.Footer>
      </Drawer>
    </>
  );
}
